import React from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { artistApi } from '../../api/artist';
import { artistIdentity, artistSectionTracking } from '../../features/browse/artist';
import ArtistContentCard from '../../features/browse/ArtistContentCard';
import ArtistShell from './ArtistShell';
import { ArtistQueryState } from './ArtistDetail';
import { normalizeArtistView, artistCategoryTitle } from './artistPresentation';
import { useArtistPageMemory } from './useArtistMemory';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
export default function ArtistViewAll() {
  const params = useParams(),
    { state } = useLocation(),
    [search] = useSearchParams(),
    { locale } = useWebLocale(),
    en = locale === 'en',
    identity = artistIdentity(params, search, state),
    view = normalizeArtistView(params.albumType);
  const query = useQuery({
    queryKey: ['artist-full-view', identity.slug, identity.sourceId, identity.sourceType, view],
    queryFn: async ({ signal }) => {
      const config = { signal, suppressGlobalError: true };
      let sourceId = identity.sourceId,
        sourceType = identity.sourceType,
        artistName = state?.artistName || search.get('artistName');
      if (!sourceId) {
        if (!identity.slug) throw Error('Invalid artist');
        const response = await artistApi.getArtistBySlug(identity.slug, {}, config);
        const a = response.data.artist;
        sourceId = a.sourceId;
        sourceType = a.artistSource;
        artistName = a.name;
      }
      const response = await artistApi.getArtistViewByType(sourceType, sourceId, view, config);
      return { artistName, sourceId, result: response.data };
    },
    staleTime: 120000,
  });
  const name =
      query.data?.artistName ||
      state?.artistName ||
      search.get('artistName') ||
      (en ? 'Artist' : '아티스트'),
    title = artistCategoryTitle(name, view, locale),
    items = query.data?.result?.items || [],
    kind =
      view === 'included-playlists' ? 'playlist' : view === 'similar-artists' ? 'artist' : 'album';
  const remember = useArtistPageMemory(!query.isPending);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.ARTIST_VIEW_ALL,
      contentType: CONTENT_TYPE.ARTIST,
      contentId: query.data?.sourceId,
      contentName: name,
      artistSection: artistSectionTracking[view],
    },
    { ready: Boolean(query.data) },
  );
  const empty = en
    ? kind === 'artist'
      ? 'No similar artists are available yet.'
      : kind === 'playlist'
        ? 'No playlists featuring this artist are available yet.'
        : 'No albums are available in this category yet.'
    : kind === 'artist'
      ? '유사한 아티스트 정보가 아직 없습니다.'
      : kind === 'playlist'
        ? '이 아티스트가 포함된 플레이리스트가 아직 없습니다.'
        : '이 분류에 등록된 앨범이 아직 없습니다.';
  return (
    <ArtistShell title={title} full position={remember}>
      <ArtistQueryState query={query}>
        {!items.length && !query.isError ? (
          <div className="pp-artist-empty">
            <h2>{en ? 'Nothing to show' : '표시할 항목이 없습니다'}</h2>
            <p>{empty}</p>
          </div>
        ) : (
          <div className="pp-artist-full-grid">
            {items.map((item, i) => (
              <ArtistContentCard
                key={`${item.artistSource || item.albumSource || ''}:${item.sourceId || item.id}-${i}`}
                item={item}
                kind={kind}
                section={view}
                focusKey={`${view}:${i}`}
                pageType={PAGE_TYPE.ARTIST_VIEW_ALL}
              />
            ))}
          </div>
        )}
      </ArtistQueryState>
    </ArtistShell>
  );
}
